import { notificationContentMatrixType, stageType } from './common'
import { MapLike } from './core'
import type { AlarmProps } from 'aws-cdk-lib/aws-cloudwatch'
import { ValidBusinessObjectList } from './datastoreProxy'
import { StackDeployDefinition } from './Deployment'
import { FargateContainerSizingConfig } from './AWS'
// import { ValidModelType } from '@iotv/datamodel';
/**
 * from file://.//../../../Identity/cognito/lib/CognitoGroupDefinitions/FW/groupDefinitions.ts
 */
export type LSCRolePolicyDefintionName =
  | 'hyperUserDefintion'
  | 'ventureTeamUserDefintion'
  | 'systemManagementDefintion'
  | 'deviceControlDefintion'
  | 'userPolicyDefintion'

/**
 * from file://.//../../../Identity/cognito/lib/policies.ts
 */
export type LSCCognitoUserPolicyName =
  | 'defaultAuthenticatedUserPolicy'
  | 'denyDeviceControllApiPolicy'
  | 'denySystemManagementApiPolicy'
  | 'deviceControllApiPolicy'
  | 'emuPolicy'
  | 'systemManagementApiPolicy'
  | 'systemManagementRoleAdditions'
  | 'uiSendNotificationPolicy'
  | 'ultrauserAssumeRolePolicy'
  | 'useLoggerBucketGetObjectS3Policy'
  | 'useLoggerBucketListS3Policy'
  | 'useLoggerBucketPutOnlyS3Policy'
  | 'userLargeObjectsRestrictedPutS3Policy'
  | 'userApiPolicy'
  | 'userBlobsListBucketPolicy'
  | 'userBlobsS3Policy'
  | 'userBlobsS3ProtectedPolicy'
  | 'userBlobsS3UploadPolicy'
  | 'userLargeObjectsDeleteS3Policy'
  | 'useLoggerBucketListS3Policy'
  | 'userLargeObjectsReadOnlyS3Policy'
  | 'userCoverageBucketGetObjectS3Policy'
  | 'userCoverageBucketListS3Policy'
  | 'wideawakeExecuteApiPolicy'
  | 'wideawakeHttpApiPolicy'

export type GroupDefinitionZombie<T extends string = string> = {
  id: T
  displayName: string
  precedence: number
  policies: LSCCognitoUserPolicyName[]
  inheritPoliciesNames: string[]
}

export type UserInvitationConfig = {
  emailSubject: string
  emailBody: string
}

export type BaseDataType = Record<string, { names?: string[]; itemProducers?: (() => Promise<ValidBusinessObjectList>)[] }>

export type ProjectFunctionProps = {
  /** this currently should not be set to more than 25 seconds as the queues have default visibility timeout of 30 secs, and cloudfromation will error if timeout is greater than this */
  maxEventAgeMs?: number
  timeoutMs?: number
  memorySizeMB?: 128 | 256 | 512 | 1024 | 2048
  reservedConcurrentExecutions?: number
  logRetentionDays?: number
  environment?: MapLike<string>
}

export type ProjectSQSEventSourceProps = {
  /**Valid Range: Minimum value of 1. Maximum value of 10. If maxBatchingWindow is configured, this value can go up to 10,000. */
  batchSize?: number
  /** Valid Range: Minimum value of 0 minutes. Maximum value of 5 minutes. */
  maxBatchingWindowSecs?: number
  /**Valid Range: Minimum value of 2. Maximum value of 1000. This cant be more than the lamdba reservedConcurrentExecutions. */
  maxConcurrency?: number
}

export type ProjectAdditionalSQSGrantProps = {
  queueName: string
  queueArn: string
}

export type LambdaAlarmConfig = Omit<AlarmProps, 'metric' | 'alarmName' | 'enabled'>

export type LambdaAlarms = {
  metricInvocations?: LambdaAlarmConfig
  metricThrottles?: LambdaAlarmConfig
  metricErrors: LambdaAlarmConfig
  metricDuration?: LambdaAlarmConfig
}

/**
 * SYNC: To keep in sync with [UI types](../../../../i301/UI/src/types/ApiTypes.ts)
 */
export type ApiEndPointName = 'NetworkApi' | 'DeviceControlApi' | 'SystemManagementApi' | 'ConsumerApi' | 'V020Api'

export type ApiEndPointConfig<AdditionalApiNames extends string> = {
  /** any standard API name or venture defined type names */
  name: ApiEndPointName | AdditionalApiNames
  endpoint: string
  region: string
}

export type UIBucketsMap = {
  /** Additional UI buckets, map with subdomain for s3, and the general name, not including project code and stage to be used to name the bucket */
  [subDomain: string]: {
    bucketName: string
    /** used in deployment */
    /** path to project base folder */
    fwRelativeBase: string
    /** path from project base folder to root of UI, eg the folder containing src from which a build will occur */
    uipath: string
  }
}

type InterfaceEndpointDeployment = {
  timestream: boolean
  cloudwatch: boolean
  sns: boolean
}
// determines which (non-mandatory) interface endpoints are deployed for the project
export type InterfaceEndpointDeploymentMap = {
  [k in stageType]: InterfaceEndpointDeployment
}

/** used in cdk definitions only, override certain default settings */
export type External_AWS_CDK_Config = {
  Cognito?: {
    groupDefinitions: GroupDefinitionZombie[]
    userInvitationConfig?: UserInvitationConfig
  }
  Lamdba?: {
    [k: string]: {
      /** overrides lambda settings such as timeout etc (depends on support in lambda cdk configuration)*/
      projectFunctionProps?: ProjectFunctionProps
      /** override sqs queues to allow (depends on support in lambda cdk configuration) */
      sqsEventSourceProps?: ProjectSQSEventSourceProps
      /** additional queues for which permissions should be granted. Generally the queueUrl needs also to be added into the target LambdaFn environment variables so it can be accessed by deployed code */
      additionalSQSQueueGrants?: ProjectAdditionalSQSGrantProps[]
      /** used by cloudwatch alerts to configure settings for this lambda */
      alarmConfig?: Partial<LambdaAlarms>
    }
  }
  S3?: {
    UIBuckets?: UIBucketsMap
  }
  ECS?: {
    /** Overrides minimal sizing used in default configuration of Wideawake, where more than 5000 devices are active use this to increase capacity */
    Wideawake?: {
      /** extenders should consider predicating the sizing on environment stage to minimize ECS costs */
      sizing: FargateContainerSizingConfig
    }
  }
  VPC?: {
    interfaceEndpointDeployment?: InterfaceEndpointDeploymentMap
  }
  /** Used in Deploy module to access code for deploying stacks defined in external project structures - see StackDeployDefinition for important notes on naming vs CloudFormation deployed stacks */
  ExternalStacks?: { [k: string]: StackDeployDefinition }
}

/** section for settings which can be used in the equivalent parts of @iotv/[module]  */
export type IoTVLambdaLayerLogicSwitchConfig = {
  ['system-provisioning']?: {
    /* allows system provisioning to query customer structure by direct hierarchy rather than the default Group structure */
    useDeprecatedCustomerHierachy?: true
  }
}

export enum GoogleChannels {
  quarterly = 'quarterly',
  beta = 'beta',
}

export type DomainMap<T extends string> = {
  [k in T]: {
    defaultSortOrder: number
  }
}

export type EnvironmentConfigShape = {
  google: {
    gcp: {
      clientId: string
      apiKey: string
      channel?: GoogleChannels
    }
  }

  // ventureTypeExtensions?: { [k in ValidModelType]?: IVentureTypesExtension };
  // ventureTypeExtensions?: IVentureTypesExtensionV2
  ventureTypeExtensions?: { [k: string]: { [j: string]: string[] } }

  /**  tld of customer public domain to deprcate in favor of. This should be undefined if no external account domain has been set up, see also externalDomain. These are due for inspection! */
  domain?: string
  /** a R53 zone in a related but not this account, this is used in a utility Stack 'ExternalDnsPlusCloudfrontForS3' used where a project has an non parentHostedZoneName is not the customer facing domain */
  externalDomain?: {
    // AccountId perhaps was tentatively introduced for where the domain is outisde the target account.
    AccountId?: string
    HostedZoneId: string
    ZoneName: string
  }
  /** the tld defined in a parent account, eg [TLD] */
  parentAccountDomain: string | undefined // tld of managing org, eg; [TLD]
  /** the subdomain of the parentAccountDomain eg dev.vxxx.[TLD] */
  internalAccountDomain: string // this target account id
  /** subdomain for sending mail eg robots in robots.[TLD] */
  mailFromSubDomain?: string
  /** account name for sending mail eg brian in brian@robots.[TLD] */
  mailFromAccountName?: string
  uiUrls?: string[] // additional urls for ui access ( cors)
  apiConfigs?: ApiEndPointConfig<string>[]
  weather?: {
    // weather api keys
    openWeather: {
      apiKey: string
    }
  }
  apps?: {
    webUI?: {
      /** defines the human-readable name to display around the app, e.g. tab name */
      ventureName?: string
      /** opt in (true) to default state attribtute useSpecializedViews so that ThingViewArbitrator uses views defined in external project ThingViewMap */
      useSpecializedViews: boolean
      /** the objectTypeId to be used in a redirect to THINGVIEW on login */
      initialRedirect: string | null
      /** a map of objectTypeIds to be displayed as groups (in addition to all deviceEnabledTypes) in menu, plus (experimentally) alternative collection names to be displayed */
      groupedObjectTypes?: {
        [objectTypeId: string]: {
          collectionName?: string
        }
      }
      /** optional domain configs */
      domainMap?: DomainMap<string>
      /** defines the email used for customer support */
      supportEmail?: string
      /** opt in (true) to display programmeSets in left menu - applies only to ventures where LSC supports scheduling of certain devices */
      showProgrammeSets?: boolean
      /** Shows privacy policy on log in screen and navigation menu */
      showPrivacyPolicy?: boolean
    }
  }
  /** overrides for CDK stacks */
  aws?: External_AWS_CDK_Config
  /** overrides for logic in @iotv/* (lambda layer) */
  iotv?: IoTVLambdaLayerLogicSwitchConfig
  /** name of the history store (in the object history) that contains values to use for the notifications system */
  notificationObjectHistory?: string | 'recentStateHistory'
  /** @deprecated in favour of Ruleset message templates */
  notificationContentMatrix?: notificationContentMatrixType
  /** @deprecated in favour of Ruleset message templates */
  //export type NotificationGenerator = (incident: Incident) => Promise<GeneratedNotificationContent>; from Datamodel
  notificationContentGenerator?: (incident: any) => Promise<any>
  /** optional definitions for data loaded into datastore at intatiation or by utility */
  BaseData?: BaseDataType
  /** support for additional APIs. These are merged into config and used to configure Amplify */
}

export const TestNoTypeExportWithouthConst = '451'
